import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import Pane from '@components/pane/pane.component';
import {DatetimeChangeEventDetail, IonCol, IonDatetime, useIonViewWillEnter} from "@ionic/react";
import {StyledAvailabilityHeader, StyledAvailabilityList, StyledAvailabilityWrapper} from "@app/availabilityPage/panes/availability.style";
import AvailabilityAddModal from "@app/availabilityPage/modals/availabilityAddModal.component";
import AvailabilityEditModal from "@app/availabilityPage/modals/availabilityEditModal.component";
import moment from "moment";
import {ReactComponent as CalendarIcon} from '@assets/images/travelAllowance/calendar.svg';
import {ReactComponent as ArrowLeftIcon} from "@assets/images/travelAllowance/arrow-left.svg"
import {ReactComponent as ArrowRightIcon} from "@assets/images/travelAllowance/arrow-right.svg"
import MyDataSkeleton from "@app/myData/components/myDataSkeleton.component";
import axios, {CancelTokenSource} from "axios";
import {Availability, CalendarEvent, Proposal} from "@models/calendar";
import AvailabilityEvent from "@app/availabilityPage/components/availabilityEvent.component";
import ProposalEvent from "@app/availabilityPage/components/proposalEvent.component";
import EventPreviewModal from "@app/availabilityPage/modals/eventPreviewModal.component";
import {getCalendarEventsByDates} from "@services/calendar/calendarEvent.service";
import {useTranslation} from "react-i18next";
import {getHoursForAvailabilityEdit} from "@utils/tools/worker";

type AvailabilityPaneProps = {
    topEdge?: number;
}

export type AvailabilityPaneHandle = {
    openAvailabilityAddModal: () => void;
}

const AvailabilityPane = forwardRef<AvailabilityPaneHandle, AvailabilityPaneProps>((props, ref) => {
    const { i18n } = useTranslation();

    const [isAvailabilityAddModalOpen, setIsAvailabilityAddModalOpen] = useState<boolean>(false);
    const [isAvailabilityEditModalOpen, setIsAvailabilityEditModalOpen] = useState<boolean>(false);
    const [isEventPreviewModalOpen, setIsEventPreviewModalOpen] = useState<boolean>(false);

    const [firstLoad, setFirstLoad] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [hoursForAvailability, setHoursForAvailability] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(moment().format());
    const [selectedAvailability, setSelectedAvailability] = useState<Availability>();
    const [selectedProposal, setSelectedProposal] = useState<Proposal>();
    const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

    const availabilityCancelToken = useRef<CancelTokenSource>();
    const datetimeRef = useRef<HTMLIonDatetimeElement>(null);
    const selectedDateRef = useRef<string>(selectedDate);
    selectedDateRef.current = selectedDate;

    useIonViewWillEnter(() => {
        (async () => {
            const hours = await getHoursForAvailabilityEdit();

            setHoursForAvailability(hours);
            await fetchCalendarEvents(selectedDateRef.current);
            setFirstLoad(true);
        })();
    }, []);

    useEffect(() => {
        moment.locale(i18n.language);
    }, [i18n.language]);

    useEffect(() => {
        if (firstLoad) {
            const el = datetimeRef.current;
            if (!el) {
                return;
            }

            let observer: MutationObserver | null = null;

            const waitForCalendar = setInterval(() => {
                const sr = el.shadowRoot;
                if (!sr) return;

                const container = sr.querySelector('.calendar-body');
                if (!container) return;

                clearInterval(waitForCalendar);

                observer = new MutationObserver(() => {
                    const firstDay = container.querySelector<HTMLElement>('.calendar-day');
                    if (!firstDay) return;

                    let month = parseInt(firstDay.dataset.month!, 10) + 1;
                    let year = parseInt(firstDay.dataset.year!, 10);

                    if (month === 13) {
                        month = 1;
                        year = year + 1;
                    }

                    const formattedMonth = month.toString().padStart(2, '0');
                    const calendarMonthDate = `${year}-${formattedMonth}-01`;

                    const now = moment();
                    const isCurrentMonth = now.year() === year && now.month() + 1 === month;

                    const selectedDate = isCurrentMonth ? now.format() : moment(calendarMonthDate).format();

                    fetchCalendarEvents(calendarMonthDate);
                    setSelectedDate(selectedDate);
                });

                observer.observe(container, {
                    childList: true,
                    subtree: true
                });
            }, 50);

            return () => {
                clearInterval(waitForCalendar);
                if (observer) observer.disconnect();
            };
        }
    }, [firstLoad]);

    useImperativeHandle(ref, () => ({
        openAvailabilityAddModal: () => {
            setIsAvailabilityAddModalOpen(true);
        },
    }));

    const handleDateSelect = (e: CustomEvent<DatetimeChangeEventDetail>) => {
        setSelectedDate(moment(e.detail.value).format());
    }

    const handlePreviousDaySelect = () => {
        setSelectedDate(moment(selectedDate).subtract(1, 'day').format());
    }

    const handleNextDaySelect = () => {
        setSelectedDate(moment(selectedDate).add(1, 'day').format());
    }

    const handleAvailabilityEditModalOpen = (availability: Availability) => {
        setSelectedAvailability(availability);
        setIsAvailabilityEditModalOpen(true);
    }

    const handleEventPreviewModalOpen = (proposal: Proposal) => {
        setSelectedProposal(proposal);
        setIsEventPreviewModalOpen(true);
    }

    const fetchCalendarEvents = async (date: string) => {
        availabilityCancelToken.current?.cancel();

        const sourceToken = axios.CancelToken.source();
        availabilityCancelToken.current = sourceToken;

        setLoading(true);

        try {
            const data = await getCalendarEventsByDates(moment(date).startOf('month').toDate(), moment(date).endOf('month').toDate());
            setCalendarEvents(data);
        } catch (error) {
            if (axios.isCancel(error)) return;
            console.error(error);
        } finally {
            if (availabilityCancelToken.current === sourceToken) {
                setLoading(false);
            }
        }
    };

    const getCalendarEventsForSelectedDate = () => {
        return calendarEvents.filter(item =>
            moment(item.startTime).isSame(selectedDate, 'day')
        );
    }

    const getDatesToHighlight = (): { date: string; backgroundColor: string; textColor?: string }[] => {
        const combined = [
            ...calendarEvents.filter(item => item.type === 'PROPOSAL'),
            ...calendarEvents.filter(item => item.type === 'AVAILABILITY')
        ];

        const byDate = new Map<string, { backgroundColor: string; textColor?: string }>();

        combined.forEach(item => {
            const date = moment(item.startTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');

            if (item.type === 'PROPOSAL') {
                const isPending = item.attributes.status === 'PROPOSED';
                const existing = byDate.get(date);

                if (!existing || existing.backgroundColor !== '#fbbc04') {
                    byDate.set(date, {
                        backgroundColor: isPending ? '#fbbc04' : '#34a853',
                        textColor: '#fff',
                    });
                }
            } else if (item.type === 'AVAILABILITY') {
                if (!byDate.has(date)) {
                    byDate.set(date, { backgroundColor: 'transparent', textColor: '#34a853' }); // tylko border
                }
            }
        });

        return Array.from(byDate.entries()).map(([date, style]) => ({
            date,
            backgroundColor: style.backgroundColor,
            textColor: style.textColor,
        }));
    };

    return (
        <Pane topEdge={props.topEdge} marginTop={40} paddingBottom={140}>
            <AvailabilityAddModal
                isOpen={isAvailabilityAddModalOpen}
                onClose={() => setIsAvailabilityAddModalOpen(false)}
                onDone={() => fetchCalendarEvents(selectedDate)}
                selectedDate={selectedDate}
                hoursForAvailability={hoursForAvailability}
            />
            {
                selectedAvailability &&
                <AvailabilityEditModal
                    isOpen={isAvailabilityEditModalOpen}
                    onClose={() => setIsAvailabilityEditModalOpen(false)}
                    onDone={() => fetchCalendarEvents(selectedDate)}
                    availability={selectedAvailability}
                    hoursForAvailability={hoursForAvailability}
                />
            }
            {
                selectedProposal &&
                <EventPreviewModal
                    isOpen={isEventPreviewModalOpen}
                    onClose={() => setIsEventPreviewModalOpen(false)}
                    onDone={() => fetchCalendarEvents(selectedDate)}
                    proposal={selectedProposal}/>
            }
            {
                !firstLoad && <MyDataSkeleton/>
            }
            {
                firstLoad && <>
                    <IonDatetime
                        ref={datetimeRef}
                        presentation="date"
                        style={{width: '100%', maxWidth: '100vw'}}
                        onIonChange={handleDateSelect}
                        value={selectedDate}
                        highlightedDates={getDatesToHighlight()}
                    />
                    {
                        loading
                            ? <MyDataSkeleton/>
                            :
                            <>
                                <StyledAvailabilityWrapper>
                                    <StyledAvailabilityHeader>
                                        <IonCol size="1">
                                            <CalendarIcon/>
                                        </IonCol>
                                        <IonCol size="9">
                                            <span className="date">{moment(selectedDate).format('dddd, D MMM YYYY')}</span>
                                        </IonCol>
                                        <IonCol size="1">
                                            <ArrowLeftIcon className="arrow" onClick={() => handlePreviousDaySelect()}/>
                                        </IonCol>
                                        <IonCol size="1">
                                            <ArrowRightIcon className="arrow" onClick={() => handleNextDaySelect()}/>
                                        </IonCol>
                                    </StyledAvailabilityHeader>
                                    <StyledAvailabilityList>
                                        {
                                            getCalendarEventsForSelectedDate().map((calendarEvent: CalendarEvent) => {
                                                if (calendarEvent.type === 'AVAILABILITY') {
                                                    return <AvailabilityEvent key={calendarEvent.id + calendarEvent.startTime} availability={calendarEvent} onClick={() => handleAvailabilityEditModalOpen(calendarEvent)}/>
                                                }

                                                if (calendarEvent.type === 'PROPOSAL') {
                                                    return <ProposalEvent key={calendarEvent.id + calendarEvent.startTime} proposal={calendarEvent} onClick={() => handleEventPreviewModalOpen(calendarEvent)}/>
                                                }

                                                return <></>
                                            })
                                        }
                                    </StyledAvailabilityList>
                                </StyledAvailabilityWrapper>
                            </>
                    }
                </>
            }
        </Pane>
    );
});

export default AvailabilityPane;
